import { Source, Layer } from 'react-map-gl/maplibre'
import type { DataDrivenPropertyValueSpecification, FilterSpecification } from 'maplibre-gl'
import { useAppStore } from '~/hooks'
import {
  FLOOD_TILE_ADAPTATION,
  FLOOD_TILE_DEM,
  FLOOD_TILE_RETURN_PERIOD,
  FLOOD_CONTOUR_SOURCE_LAYER,
  FLOOD_CONTOUR_SOURCES,
  FLOOD_CONTOUR_URLS,
  FLOOD_DEPTH_BREAKS,
  FLOOD_DEPTH_COLORMAP,
  LayerId,
  VisibleLayers,
  type AdaptationCode,
} from '~/constants'
import { floodDepthFillColor } from '~/util/floodContours'

function ContourFloodLayer() {
  const { layerVisibility, dataset, returnPeriod, DEM, adaptation, contourFloodEnabled } =
    useAppStore()

  const floodOn = dataset === 'cosmos' && layerVisibility[VisibleLayers.FLOOD]
  // ponytail: keep layout.visible so MapLibre continues to cache tiles; hide via opacity
  // (visibility:none tends to drop/reload tiles on each Extent↔Depth toggle).
  const active = floodOn && contourFloodEnabled

  const paint = {
    'fill-color': floodDepthFillColor(
      FLOOD_DEPTH_BREAKS,
      FLOOD_DEPTH_COLORMAP
    ) as DataDrivenPropertyValueSpecification<string>,
    'fill-opacity': active ? 0.85 : 0,
  }

  const filter =
    returnPeriod && DEM && adaptation
      ? ([
          'all',
          ['==', ['get', 'adaptation'], FLOOD_TILE_ADAPTATION[adaptation as AdaptationCode]],
          ['==', ['get', 'dem'], FLOOD_TILE_DEM[DEM as keyof typeof FLOOD_TILE_DEM]],
          [
            '==',
            ['get', 'return_period'],
            FLOOD_TILE_RETURN_PERIOD[returnPeriod as keyof typeof FLOOD_TILE_RETURN_PERIOD],
          ],
        ] as FilterSpecification)
      : (['literal', false] as FilterSpecification)

  return (
    <>
      {FLOOD_CONTOUR_SOURCES.map((source) => {
        const url = FLOOD_CONTOUR_URLS[source]
        if (!url) return null

        return (
          <Source key={source} id={source} type="vector" url={`pmtiles://${url}`}>
            <Layer
              id={`${source}-fill`}
              source={source}
              source-layer={FLOOD_CONTOUR_SOURCE_LAYER}
              type="fill"
              beforeId={LayerId.BUILDING}
              filter={filter}
              paint={paint}
              layout={{
                visibility: floodOn ? 'visible' : 'none',
                'fill-sort-key': ['get', 'contour'],
              }}
            />
          </Source>
        )
      })}
    </>
  )
}

export default ContourFloodLayer
